<template>
  <div @click="setData" :class="{selected: isSelected}" class="plan-card">
    <div class="plan-card__select" />

    <div class="plan-card__discount">
      <div v-if="addDiscount" class="plan-card__discount_add plan-card__discount_card">
        {{ $t('addDiscount') }} {{ addDiscount }} %
      </div>
      <div v-if="discountPercent" class="plan-card__discount_main plan-card__discount_card">
        {{ $t('discount') }} {{ discountPercent }} %
      </div>
    </div>

    <template v-if="addDiscount">
      <div class="plan-card__price-old">
        {{ formatCurrency(price,currency) }}
      </div>
      <div class="plan-card__price">
        {{ formatCurrency(newPrice,currency) }}
      </div>
    </template>

    <template v-else>
      <div class="plan-card__price">
        {{ formatCurrency(price,currency) }}
      </div>
    </template>

    <span>{{ $t('perMonth') }}</span>
    <div class="plan-card__wholesale">
      <span v-if="duration > 1">
        <PaymentPeriodPrice
          v-if="plan && period"
          :plan="plan"
          :period="period"
          :new-price="newPrice"
          :add-discount="addDiscount"
        />
      </span>
      {{ $tc('every', duration) }} {{ duration }} {{ $tc('months', duration) }}
    </div>
    <div class="plan-card__current" v-if="currentPeriodItem.id === period.id">
      <div>{{ $t('current') }}</div>
    </div>
  </div>
</template>

<script>
import { computed, toRefs } from '@vue/composition-api';
import useNumberFormatter from '@/compositions/useNumberFormatter';
import useAccount from '@/compositions/accounting/useAccount';
import PaymentPeriodPrice from '@/components/Payment/Periods/PaymentPeriodPrice';
import useDiscount from '@/compositions/useDiscount';

export default {
  name: 'PaymentDialogModalPeriodItem',
  components: { PaymentPeriodPrice },
  props: {
    period: {
      type: Object,
      required: true,
    },
    plan: {
      type: Object,
      required: true,
    },
    selected: {
      type: Object,
      default: null,
    },
  },
  setup(props, { emit }) {
    const { period, plan, selected } = toRefs(props);
    const { discountPercent, duration } = period.value;
    const { formatCurrency } = useNumberFormatter();
    const { period: currentPeriod, account } = useAccount();
    const { discountInfo } = useDiscount();

    const price = computed(() => {
      return ((100 - period.value.discountPercent) / 100) * plan.value.price.value;
    });

    const discount = computed(() => {
      return plan.value.price.value - price.value;
    });

    const addDiscount = computed(() => discountInfo.value[0]?.discount);

    const newPrice = computed(() => {
      return ((100 - addDiscount.value) / 100) * price.value;
    });

    function setData() {
      emit('input', period.value);
    }

    const currentPeriodItem = computed(() => account.value.nextPeriod || currentPeriod.value || {});

    return {
      discountPercent,
      price,
      total: computed(() => price.value * duration),
      duration,
      discount,
      currency: computed(() => plan.value.price.currency),
      formatCurrency,
      setData,
      isSelected: computed(() => {
        return selected.value?.id === period.value.id || (!selected.value && currentPeriodItem.value?.id === period.value.id);
      }),
      currentPeriodItem,
      addDiscount,
      newPrice,
    };
  },
};
</script>

<style lang="scss" scoped>
.plan-card {
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding: 1.3125rem;
  width: 17.5rem;
  max-width: 100%;
  min-height: 13.25rem;
  text-align: center;
  color: #212121;
  font-weight: 400;
  border: 1px solid #EEEEEE;
  border-radius: 0.5rem;
  position: relative;
  cursor: pointer;
  &.selected {
    border: 2px solid #1FC57F;
    .plan-card__select {
      border: 0.3125rem solid #0A82D9;
    }
  }
  &__select {
    width: 1.125rem;
    height: 1.125rem;
    border-radius: 50%;
    border: 2px solid #757575;
    position: absolute;
    top: 1.3125rem;
    left: 1.3125rem;
  }
  &__discount {
    position: absolute;
    top: 0.3125rem;
    right: 0.3125rem;
    display: flex;
    gap: 5px;
    &_card {
      background: #FFF3EA;
      border: 1px solid #FFE8D7;
      border-radius: 0.25rem;
      font-size: 0.75rem;
      line-height: 1.67;
      color: #FE8630;
      padding: 0 0.3125rem;
    }
    &_add {
      background: #C2EEDC;
      color: #1FC57F;
      border-color: #C2EEDC;
    }
  }
  &__current {
    line-height: 1.375;
    text-align: center;
    margin-bottom: 1.25rem;
    color: #1FC57F;
    position: absolute;
    top: 0.5rem;
    left: 0;
    right: 0;
  }
  &__price {
    font-size: 1.5rem;
    line-height: 0.92;
    margin-bottom: 0.375rem;
  }
  &__price-old {
    text-decoration: line-through;
    color: rgba(33, 33, 33, 0.5);
    text-decoration-color: rgba(33, 33, 33, 0.5);
    margin-top: 30px;
  }
  &__wholesale {
    font-size: 0.875rem;
    line-height: 1.57;
    margin-top: 30px;
  }
}
@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .plan-card {
    padding: 1.875rem 1.25rem 1.25rem 1.25rem;
    width: 15.4rem;
    &__discount {
      flex-direction: column;
    }
  }
}
@media #{map-get($display-breakpoints, 'xs-only')} {
  .plan-card {
    display: block;
    padding: 0.875rem 0.9375rem 0.75rem 2.6875rem;
    width: 100%;
    min-height: 4.375rem;
    text-align: left;
    &__select {
      top: 1rem;
      left: 0.9375rem;
    }
    &__current {
      font-size: 0.8125rem;
      text-align: left;
      position: static;
      margin-bottom: 0;
    }
    &__price {
      display: inline-block;
      font-size: 1rem;
      line-height: 0.92;
      margin: 0 0.25rem 0.125rem 0;
    }
    &__wholesale {
      font-size: 0.8125rem;
      line-height: 1.4;
      position: static;
      color: #757575;
    }
    &__discount {
      flex-direction: row;
    }
  }
}

@media screen and (max-width: 345px) {
  .plan-card {
    &__discount {
      flex-direction: column;
    }
  }
}

</style>

<i18n lang="yml" locale="ru">
discount: Скидка
current: Текущий
perMonth: в месяц
every: каждые | каждый | каждые | каждые
months: месяцев | месяц | месяца | месяцев
addDiscount: Доп.скидка
</i18n>
<i18n lang="yml" locale="en">
discount: Discount
current: Current
perMonth: per month
every: every
months: months | month | months
addDiscount: Add a discount
</i18n>
